/**
 * Busca de endereço pelo CEP — BrasilAPI, CEP v2.
 * https://brasilapi.com.br/docs#tag/CEP-V2
 *
 * Chamada direto do navegador (a BrasilAPI responde com CORS aberto) e com
 * `fetch` puro, não o `apiFetch`: ele manda o cookie de sessão e trata 401
 * como sessão vencida da NOSSA API, e nenhuma das duas coisas vale aqui.
 */

const BASE = 'https://brasilapi.com.br/api/cep/v2';

export interface EnderecoPorCep {
    /** Mascarado: 00000-000. */
    cep: string;
    /** `null` em CEP de cidade inteira — a cidade não tem CEP por rua. */
    logradouro: string | null;
    bairro: string | null;
    cidade: string;
    estado: string;
}

/**
 * A BrasilAPI responde 404 tanto para CEP inexistente quanto para "todos os
 * provedores falharam", e o corpo não separa os dois com segurança. Por isso
 * 404 vira `nao-encontrado`, e só rede caída, 429 e 5xx viram `indisponivel`.
 */
export type ResultadoCep =
    | { tipo: 'achado'; endereco: EnderecoPorCep }
    | { tipo: 'nao-encontrado' }
    | { tipo: 'indisponivel' };

interface RespostaV2 {
    cep: string;
    state: string;
    city: string;
    neighborhood: string | null;
    street: string | null;
}

export const buscarCep = async (cep: string, signal?: AbortSignal): Promise<ResultadoCep> => {
    const digitos = cep.replace(/\D/g, '');
    if (digitos.length !== 8) return { tipo: 'nao-encontrado' };

    let response: Response;
    try {
        response = await fetch(`${BASE}/${digitos}`, { signal });
    } catch (e) {
        // Cancelada por quem chamou: sobe, para não virar "fora do ar".
        if (e instanceof DOMException && e.name === 'AbortError') throw e;
        return { tipo: 'indisponivel' };
    }

    if (response.status === 404 || response.status === 400) return { tipo: 'nao-encontrado' };
    if (!response.ok) return { tipo: 'indisponivel' };

    const r: RespostaV2 = await response.json();
    return {
        tipo: 'achado',
        endereco: {
            cep: mascararCep(r.cep),
            logradouro: r.street?.trim() || null,
            bairro: r.neighborhood?.trim() || null,
            cidade: r.city,
            estado: r.state,
        },
    };
};

/**
 * O que fazer com a rua e o bairro que já estavam no formulário quando a
 * BrasilAPI não devolve os dela (CEP de cidade inteira). Rua e bairro são de
 * uma cidade: ficam se a cidade é a mesma ou ainda estava em branco — quem
 * digitou a rua antes do CEP não perde nada —, e saem se a cidade mudou,
 * senão sobra o bairro de São Paulo ao lado de Poconé/MT.
 */
export const manterSeMesmaCidade = (
    endereco: EnderecoPorCep,
    atual: { cidade: string; estado: string },
): boolean => {
    const norm = (s: string) => s.trim().toLocaleLowerCase('pt-BR');
    if (!atual.cidade.trim()) return true;
    return norm(atual.cidade) === norm(endereco.cidade)
        && (!atual.estado.trim() || norm(atual.estado) === norm(endereco.estado));
};

/** Máscara progressiva, para digitar no campo: 01310100 → 01310-100. */
export const mascararCep = (valor: string): string => {
    const d = valor.replace(/\D/g, '').slice(0, 8);
    return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
};
